import DocumentHeader from '@components/DocumentHeader';
import SkyscraperBackground from '@components/SkyscraperBackground';
import If from '@components/If';
import { ErrorNotification } from './styled';
import { shallowEqual, useSelector } from 'react-redux';
import type { LocationState } from 'redux-first-router';
import { useQuery } from 'react-query';
import type { AxiosError } from 'axios';
import { getAccessTokenFromOtp, deleteAccount } from '@simplywallst/services';
import { clearAllCookies } from '@utilities/cookies';
import { useEffect } from 'react';

export function Offboarding() {
  const token = useSelector<{ location: LocationState }, string | undefined>(
    (state) => state.location.payload.token,
    shallowEqual
  );

  const deactivateQuery = useQuery(
    ['deactivate-account', token],
    async (params) => {
      /** We've already null checked `token` in the `enabled` option */
      const token = params.queryKey[1]!;
      let accessTokenResponse:
        | undefined
        | Awaited<ReturnType<typeof getAccessTokenFromOtp>>;
      try {
        accessTokenResponse = await getAccessTokenFromOtp({ auth_otp: token });

        if (accessTokenResponse.data.confirm_email === 'stale') {
          throw new Error('Account is stale');
        }
      } catch (unknownError) {
        const axiosError = unknownError as AxiosError;
        if (
          axiosError.isAxiosError &&
          typeof axiosError.response !== 'undefined'
        ) {
          throw new Error(axiosError.response.data.errors[0].detail);
        } else {
          throw unknownError;
        }
      }

      let response: undefined | Awaited<ReturnType<typeof deleteAccount>>;
      try {
        /**
         *
         * We'll handcraft the auth bearer here instead
         * of using `withAuth`. `withAuth` reads the token from
         * the cookie and writing a cookie just to delete
         * it immediately after this request seems non-optimal.
         *
         */
        response = await deleteAccount(token, {
          Authorization: `Bearer ${accessTokenResponse.data.access_token}`,
        });
      } catch (unknownError) {
        const axiosError = unknownError as AxiosError;
        if (
          axiosError.isAxiosError &&
          typeof axiosError.response !== 'undefined'
        ) {
          throw new Error(axiosError.response.data.errors[0].title);
        } else {
          throw unknownError;
        }
      }
      return response;
    },
    {
      enabled: typeof token === 'string',
      retry: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (typeof token === 'string') {
      clearAllCookies();
    }
  }, [token]);
  return (
    <SkyscraperBackground>
      <DocumentHeader title="Deactivate account" />
      <If
        conditions={{
          success: deactivateQuery.status === 'success',
          error: deactivateQuery.status === 'error',
          loading: deactivateQuery.status === 'loading',
        }}
      >
        {{
          success: () => (
            <>
              <h3 data-cy-id="delete-success-header">Account Deleted</h3>
              <p>
                If you have time to spare your feedback is greatly appreciated.
              </p>
              <p>
                <a
                  href={`mailto:feedback@simplywallst.com?subject=Feedback;`}
                  rel="nofollow"
                >
                  Submit feedback
                </a>
              </p>
            </>
          ),
          error: () => (
            <>
              <h3>Account Error</h3>
              <p>An error has occured while trying to delete your account.</p>
              <ErrorNotification alert data-cy-id="error-message">
                {(deactivateQuery.error as Error)?.message}
              </ErrorNotification>
            </>
          ),
          loading: () => (
            <>
              <h3>Deleting account</h3>
              <p>Please wait</p>
            </>
          ),
        }}
      </If>
    </SkyscraperBackground>
  );
}
